/**
 * The guided-flow parts of the operational workspace: where am I (the
 * progress line) and what have I chosen (the choice summary) above the step;
 * what it adds up to (the summary outcome) and one move forward with one move
 * back at the end of the step.
 */
import React from 'react';
import { FmButton } from './FmButton';
import { FmElementView } from './elements/FmElement';
import { FmElementContextProvider } from './elements/elementContext';
import { guidedChoicesMade, guidedTransitionMoves } from './FmOperationalGuidance.helpers';
import './FmOperationalWorkspaceRoot.guidance.css';
import type {
  FmOperationalGuidanceDescriptor,
  FmOperationalStageDescriptor,
} from './FmOperationalWorkspaceRoot.types';

function progressText(
  guidance: FmOperationalGuidanceDescriptor,
  index: number,
  count: number,
  label: string,
): string {
  return guidance.progressLabel
    .replace('{index}', String(index + 1))
    .replace('{count}', String(count))
    .replace('{label}', label);
}

export function GuidedStepHeader({
  guidance,
  stages,
  activeStage,
  testId,
  onStageChange,
}: {
  guidance: FmOperationalGuidanceDescriptor;
  stages: readonly FmOperationalStageDescriptor[];
  activeStage: FmOperationalStageDescriptor;
  testId: string;
  onStageChange: (stageId: string) => void;
}): React.ReactElement {
  const index = stages.findIndex((stage) => stage.id === activeStage.id);
  const summary = guidance.summary;
  const { items } = guidedChoicesMade(guidance, stages, activeStage.id);
  // The compact chrome carries the choices in its docked strip instead.
  const panel = guidance.chrome !== 'compact' && summary && items.length > 0;
  return (
    <div
      className="fm-operational-workspace__guide"
      data-chrome={guidance.chrome === 'compact' ? 'compact' : undefined}
      data-testid={`${testId}-guide`}
    >
      <p className="fm-operational-workspace__progress" data-testid={`${testId}-progress`}>
        {progressText(guidance, index, stages.length, activeStage.label)}
      </p>
      {panel ? (
        <section
          className="fm-operational-workspace__choices"
          aria-label={summary.title}
          data-testid={`${testId}-choices`}
        >
          <p className="fm-operational-workspace__choices-title">{summary.title}</p>
          <dl className="fm-operational-workspace__choice-list">
            {items.map((item, itemIndex) => {
              const stage = stages.find((candidate) => candidate.id === item.stageId);
              // One way back per step: several choices from the same step
              // offer it on the first only, not one identical link each.
              const firstOfStage =
                items.findIndex((other) => other.stageId === item.stageId) === itemIndex;
              const canChange =
                !summary.readOnly &&
                firstOfStage &&
                stage &&
                stage.id !== activeStage.id &&
                stage.status !== 'blocked';
              return (
                <div
                  key={`${item.stageId}-${item.label}`}
                  className="fm-operational-workspace__choice"
                >
                  {/* The way back rides on the label's line, so a value keeps
                      the full width and never wraps around it on a phone. */}
                  <dt>
                    <span>{item.label}</span>
                    {canChange ? (
                      <FmButton
                        variant="text"
                        size="sm"
                        className="fm-operational-workspace__choice-change"
                        aria-label={`${summary.changeLabel}: ${item.label}`}
                        onClick={() => onStageChange(item.stageId)}
                      >
                        {summary.changeLabel}
                      </FmButton>
                    ) : null}
                  </dt>
                  <dd>{item.value}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ) : null}
    </div>
  );
}

/**
 * What the choices add up to, right after the step's own work (its dominant
 * region): above the step it would push the map and the primary action off
 * the first screen, and after every supporting region it would read as an
 * afterthought to what was left out.
 */
export function GuidedStepOutcome({
  guidance,
  testId,
}: {
  guidance: FmOperationalGuidanceDescriptor;
  testId: string;
}): React.ReactElement | null {
  const outcome = guidance.summary?.outcome;
  if (!outcome) return null;
  return (
    <div className="fm-operational-workspace__outcome" data-testid={`${testId}-outcome`}>
      <FmElementContextProvider
        value={{ base: `${testId}-outcome`, contrast: 'light', headingLevel: 3 }}
      >
        <FmElementView element={outcome} />
      </FmElementContextProvider>
    </div>
  );
}

/** One primary move forward and one move back; the step's own words name both. */
export function GuidedStepTransition({
  guidance,
  stages,
  activeStage,
  onStageChange,
}: {
  guidance: FmOperationalGuidanceDescriptor;
  stages: readonly FmOperationalStageDescriptor[];
  activeStage: FmOperationalStageDescriptor;
  onStageChange: (stageId: string) => void;
}): React.ReactElement | null {
  const { back, forward, blockedReason, sentence } = guidedTransitionMoves(
    guidance,
    stages,
    activeStage,
  );
  const reasonId = `${activeStage.id}-transition-reason`;
  if (!back && !forward && !sentence) return null;
  return (
    <div
      className="fm-operational-workspace__transition"
      data-guided="true"
      data-final={forward ? undefined : ''}
    >
      {back ? (
        <FmButton variant="bordered" onClick={() => onStageChange(back.id)}>
          {guidance.previousLabel}
        </FmButton>
      ) : null}
      {sentence ? <p id={blockedReason ? reasonId : undefined}>{sentence}</p> : null}
      {forward ? (
        <FmButton
          variant="primary"
          disabled={forward.status === 'blocked'}
          aria-describedby={blockedReason ? reasonId : undefined}
          onClick={() => onStageChange(forward.id)}
          data-testid={`guided-transition-${forward.id}`}
        >
          {activeStage.transitionActionLabel}
        </FmButton>
      ) : null}
    </div>
  );
}
